/* eslint-disable @typescript-eslint/no-explicit-any */
import { CouponType } from '@/api/gql/graphql';
import type { CouponOutput, CouponUpdateInput } from '@/api/gql/graphql';
import {
  useCreateCoupons,
  useGetCoupons,
  useUpdateCoupons,
} from '@/api/hooks/useCoupon';

import { createMenuItem } from '@/components/Common/DropdownMenu/MenuDropdown.utils';
import { ColumnDefinition } from '@/components/Ui/DataTable/DataTable.type';
import { CouponFormValues, useHandleCouponForm } from '@/hooks/useCouponForm';
import { useFormatDate } from '@/hooks/useFormatDate';
import { useToast } from '@/hooks/useToast';
import { useCouponStore } from '@/store/CouponStore/CouponStore';
import { useFilterStore } from '@/store/useFilterOptionsStore';
import { useEffect, useState } from 'react';
import { usePackage } from './package.hook';

const columns: ColumnDefinition[] = [
  { field: 'code', header: 'Code promo' },
  { field: 'discountValue', header: 'Reduction' },
  { field: 'package', header: 'Package' },
  { field: 'usageLimit', header: 'Usage' },
  { field: 'usedCount', header: "Nbre d'utilisation" },
  { field: 'discountType', header: 'Type' },
  { field: 'expirationDate', header: 'expirationDate' },
  { field: 'status', header: 'Statut' },
  { field: 'action', header: 'Action', sortable: false },
];

export enum Status {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}

export const statusConfig: Record<Status, { label: string; color: string }> = {
  [Status.INACTIVE]: { label: 'Invalide', color: 'text-completed' },
  [Status.ACTIVE]: { label: 'Validé', color: 'text-running' },
};

export type FormMode = 'create' | 'update';

export const useCoupons = () => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [discountType, setDiscountType] = useState<CouponType>(
    CouponType.Fixed,
  );
  const { data: couponList, isLoading: loadCoupons } = useGetCoupons();

  const { filterSelections, setIsOpenFilter } = useFilterStore();
  const { formatDate } = useFormatDate();

  const { mutate: createCoupon, isPending: isCreatePending } =
    useCreateCoupons();
  const { mutate: updateCoupon, isPending: isUpdatePending } =
    useUpdateCoupons();

  const {
    currentPageTitle,
    setCurrentPageTitle,
    openCouponFormModal,
    isOpenCouponFormModal,
    setCouponListPage,
    couponListPage,
    couponListLimit,
    setCouponListLimit,
    couponId,
    setCouponId,
    coupon,
    setCoupon,
    formMode,
    setFormMode,
  } = useCouponStore();

  const { packages, openCreatePackage, loadPackages } = usePackage();
  const { showToast } = useToast();

  const { register, handleSubmit, errors, reset, setValue } =
    useHandleCouponForm('create', discountType ?? 'FIXED');

  const {
    register: registerUpdateForm,
    handleSubmit: handleSubmitUpdate,
    errors: updateErrors,
    setValue: setUpdateValue,
    reset: resetUpdate,
  } = useHandleCouponForm('update', discountType ?? 'FIXED');

  const statusType = [
    { _id: CouponType.Fixed, name: CouponType.Fixed },
    { _id: CouponType.Percent, name: CouponType.Percent },
  ];

  const openUpdateModal = () => {
    openCouponFormModal();
    setFormMode('update');
  };

  const openCreateModal = () => {
    setFormMode('create');
    openCouponFormModal();
  };

  const interuptCoupon = () => {
    openCouponFormModal();
  };

  const menuItems = [
    createMenuItem('Modifier', '', openUpdateModal),
    createMenuItem('Interrompre', '', interuptCoupon),
    createMenuItem('Supprimer', 'font-[400] text-[#EA242C]', () =>
      alert('Utilisateur supprimé'),
    ),
  ];

  const formattedList = couponList?.map((item: CouponOutput) => {
    const date = new Date(item.expirationDate);
    const formattedDate = date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    const list = {
      ...item,
      expirationDate: formattedDate,
    };
    return list;
  });

  const handleSelectedDiscountType = (value: any) => {
    setDiscountType(value.name);
  };

  const createCouponHandler = (values: CouponFormValues) => {
    createCoupon(
      {
        discountType: values.discountType as CouponType,
        discountValue: values.value ?? 0,
        usageLimit: values.usageLimit ?? 0,
        expirationDate: values.expirationDate,
        packages: (values.package && values.package.map((pk) => pk._id)) || [],
        customCode: values.code,
      },
      {
        onSuccess: () => {
          showToast('Coupon créé', 'success', {
            autoClose: 3000,
            position: 'top-right',
          });

          reset();
          openCouponFormModal();
        },
        onError: (error: { message: string }) => {
          showToast(error?.message, 'error', {
            autoClose: 3000,
            position: 'top-right',
          });
        },
      },
    );
  };

  const updateCouponHandler = (values: any) => {
    const coupon = Object.keys(values).reduce((acc: any, key: string) => {
      const value = values[key];
      if (value !== null && value !== undefined) {
        if (key === 'code') {
          acc.customCode = value;
        }
        if (key === 'value') {
          acc.discountValue = value;
        }
        if (key === 'package') {
          acc.packages = [value];
        } else {
          acc[key] = value;
        }
      }
      return acc;
    }, {} as CouponUpdateInput);

    coupon.discountType = values.discountType as CouponType;
    delete coupon.code;
    delete coupon.value;

    updateCoupon(
      {
        input: coupon,
        updateCouponId: couponId,
      },
      {
        onSuccess: () => {
          showToast('Coupon mis à jour', 'success', {
            autoClose: 3000,
            position: 'top-right',
          });
          resetUpdate();
          openCouponFormModal();
        },
        onError: (error: { message: string }) => {
          showToast(error?.message, 'error', {
            autoClose: 3000,
            position: 'top-right',
          });
        },
      },
    );
  };

  useEffect(() => {
    if (couponId) {
      const coupon = couponList?.find(
        (cp: CouponOutput) => cp._id === couponId,
      );
      setCoupon(coupon || null);
    }
  }, [couponId, couponList, setCoupon]);

  return {
    activeTab,
    setActiveTab,
    couponList: formattedList,
    loadCoupons,
    columns,
    statusConfig,
    filterSelections,
    setIsOpenFilter,
    currentPageTitle,
    setCurrentPageTitle,
    isOpenCouponFormModal,
    openCouponFormModal,
    createCouponHandler,
    updateCouponHandler,
    register,
    handleSubmit,
    errors,
    reset,
    setValue,
    handleSelectedDiscountType,
    statusType,
    setCouponListPage,
    couponListPage,
    couponListLimit,
    setCouponListLimit,
    packages,
    discountType,
    menuItems,
    couponId,
    setCouponId,
    registerUpdateForm,
    handleSubmitUpdate,
    updateErrors,
    setUpdateValue,
    resetUpdate,
    updateCoupon,
    formMode,
    setFormMode,
    openCreateModal,
    coupon,
    formatDate,
    isUpdatePending,
    isCreatePending,
    openCreatePackage,
    loadPackages,
  };
};
